/**
 * CanvasNavigationBar.tsx
 * Barre de navigation complète pour le canvas avec zoom, pan, reset, etc.
 */
import React, { useCallback } from 'react';
import { useAppStore } from '../../../services/StateService';
import Konva from 'konva';

interface CanvasNavigationBarProps {
  stageRef: React.RefObject<Konva.Stage | null>;
  containerRef: React.RefObject<HTMLDivElement | null>;
  onStagePositionChange?: (position: { x: number; y: number }) => void;
}

export function CanvasNavigationBar({
  stageRef,
  containerRef,
  onStagePositionChange,
}: CanvasNavigationBarProps) {
  const zoomLevel = useAppStore(state => state.canvas.zoomLevel);
  const setZoomLevel = useAppStore(state => state.setZoomLevel);
  const templateWidth = useAppStore(state => state.canvas.templateWidth);
  const templateHeight = useAppStore(state => state.canvas.templateHeight);

  const handleZoomIn = useCallback(() => {
    const stage = stageRef.current;
    if (!stage) return;

    const oldScale = stage.scaleX();
    const newScale = Math.min(5, oldScale * 1.2);
    const pointer = stage.getPointerPosition() || { x: stage.width() / 2, y: stage.height() / 2 };

    const mousePointTo = {
      x: (pointer.x - stage.x()) / oldScale,
      y: (pointer.y - stage.y()) / oldScale,
    };

    stage.scale({ x: newScale, y: newScale });

    const newPos = {
      x: pointer.x - mousePointTo.x * newScale,
      y: pointer.y - mousePointTo.y * newScale,
    };
    stage.position(newPos);
    onStagePositionChange?.(newPos);
    stage.batchDraw();
    setZoomLevel(newScale);
  }, [stageRef, setZoomLevel, onStagePositionChange]);

  const handleZoomOut = useCallback(() => {
    const stage = stageRef.current;
    if (!stage) return;

    const oldScale = stage.scaleX();
    const newScale = Math.max(0.1, oldScale / 1.2);
    const pointer = stage.getPointerPosition() || { x: stage.width() / 2, y: stage.height() / 2 };

    const mousePointTo = {
      x: (pointer.x - stage.x()) / oldScale,
      y: (pointer.y - stage.y()) / oldScale,
    };

    stage.scale({ x: newScale, y: newScale });

    const newPos = {
      x: pointer.x - mousePointTo.x * newScale,
      y: pointer.y - mousePointTo.y * newScale,
    };
    stage.position(newPos);
    onStagePositionChange?.(newPos);
    stage.batchDraw();
    setZoomLevel(newScale);
  }, [stageRef, setZoomLevel, onStagePositionChange]);

  const handleZoomReset = useCallback(() => {
    const stage = stageRef.current;
    if (!stage) return;

    const newPos = { x: 0, y: 0 };
    stage.scale({ x: 1, y: 1 });
    stage.position(newPos);
    onStagePositionChange?.(newPos);
    stage.batchDraw();
    setZoomLevel(1);
  }, [stageRef, setZoomLevel, onStagePositionChange]);

  const handleFitToScreen = useCallback(() => {
    const stage = stageRef.current;
    const container = containerRef.current;
    if (!stage || !container) return;

    const containerRect = container.getBoundingClientRect();
    const containerWidth = containerRect.width;
    const containerHeight = containerRect.height;

    // Calculer le scale pour fit-to-screen
    const scaleX = (containerWidth - 100) / templateWidth;
    const scaleY = (containerHeight - 100) / templateHeight;
    const scale = Math.min(scaleX, scaleY) * 0.9; // 90% pour laisser de la marge

    stage.scale({ x: scale, y: scale });

    // Centrer le canvas
    const scaledWidth = templateWidth * scale;
    const scaledHeight = templateHeight * scale;
    const offsetX = 50; // Offset du canvas
    const offsetY = 50;

    const newPos = {
      x: (containerWidth - scaledWidth) / 2 - offsetX * scale,
      y: (containerHeight - scaledHeight) / 2 - offsetY * scale,
    };
    stage.position(newPos);
    onStagePositionChange?.(newPos);
    stage.batchDraw();
    setZoomLevel(scale);
  }, [stageRef, containerRef, templateWidth, templateHeight, setZoomLevel, onStagePositionChange]);

  const handleCenterCanvas = useCallback(() => {
    const stage = stageRef.current;
    const container = containerRef.current;
    if (!stage || !container) return;

    const containerRect = container.getBoundingClientRect();
    const containerWidth = containerRect.width;
    const containerHeight = containerRect.height;

    const scaledWidth = templateWidth * stage.scaleX();
    const scaledHeight = templateHeight * stage.scaleY();
    const offsetX = 50 * stage.scaleX();
    const offsetY = 50 * stage.scaleY();

    const newPos = {
      x: (containerWidth - scaledWidth) / 2 - offsetX,
      y: (containerHeight - scaledHeight) / 2 - offsetY,
    };
    stage.position(newPos);
    onStagePositionChange?.(newPos);
    stage.batchDraw();
  }, [stageRef, containerRef, templateWidth, templateHeight, onStagePositionChange]);

  // AMÉLIORATION: Handler pour le slider de zoom
  const handleZoomSliderChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const stage = stageRef.current;
      if (!stage) return;

      const newScale = parseFloat(e.target.value);
      const pointer = stage.getPointerPosition() || { x: stage.width() / 2, y: stage.height() / 2 };
      const oldScale = stage.scaleX();

      const mousePointTo = {
        x: (pointer.x - stage.x()) / oldScale,
        y: (pointer.y - stage.y()) / oldScale,
      };

      stage.scale({ x: newScale, y: newScale });

      const newPos = {
        x: pointer.x - mousePointTo.x * newScale,
        y: pointer.y - mousePointTo.y * newScale,
      };
      stage.position(newPos);
      onStagePositionChange?.(newPos);
      stage.batchDraw();
      setZoomLevel(newScale);
    },
    [stageRef, setZoomLevel, onStagePositionChange]
  );

  // AMÉLIORATION: Zoom vers la sélection
  const handleZoomToSelection = useCallback(() => {
    const stage = stageRef.current;
    const container = containerRef.current;
    if (!stage || !container) return;

    const store = useAppStore.getState();
    const selectedIds = store.selection.selectedIds || [];
    if (selectedIds.length === 0) {
      handleFitToScreen();
      return;
    }

    // Calculer les bounds de la sélection
    const shapes = store.shapes.filter(s => selectedIds.includes(s.id));
    if (shapes.length === 0) return;

    let minX = Infinity,
      minY = Infinity,
      maxX = -Infinity,
      maxY = -Infinity;
    shapes.forEach(shape => {
      minX = Math.min(minX, shape.x || 0);
      minY = Math.min(minY, shape.y || 0);
      maxX = Math.max(maxX, (shape.x || 0) + (shape.width || 0));
      maxY = Math.max(maxY, (shape.y || 0) + (shape.height || 0));
    });

    const selectionWidth = maxX - minX;
    const selectionHeight = maxY - minY;
    const centerX = minX + selectionWidth / 2;
    const centerY = minY + selectionHeight / 2;

    const containerRect = container.getBoundingClientRect();
    const containerWidth = containerRect.width;
    const containerHeight = containerRect.height;

    // Calculer le scale pour fit la sélection
    const scaleX = (containerWidth - 200) / selectionWidth;
    const scaleY = (containerHeight - 200) / selectionHeight;
    const scale = Math.min(scaleX, scaleY, 5) * 0.9; // Limiter à 500%

    stage.scale({ x: scale, y: scale });

    // Centrer sur la sélection
    const newPos = {
      x: containerWidth / 2 - centerX * scale,
      y: containerHeight / 2 - centerY * scale,
    };
    stage.position(newPos);
    onStagePositionChange?.(newPos);
    stage.batchDraw();
    setZoomLevel(scale);
  }, [stageRef, containerRef, setZoomLevel, onStagePositionChange, handleFitToScreen]);

  // AMÉLIORATION: Écouter les événements de navigation depuis KeyboardManager
  // P3 : les wrappers doivent appeler les vraies fonctions externes — les
  // locals masquaient ce nom et créaient une récursion infinie. Renommés.
  React.useEffect(() => {
    const onCenterView = () => handleCenterCanvas();
    const onZoomFit = () => handleFitToScreen();
    const onZoomReset = () => handleZoomReset();
    const onZoomIn = () => handleZoomIn();
    const onZoomOut = () => handleZoomOut();

    window.addEventListener('canvas-center-view', onCenterView);
    window.addEventListener('canvas-zoom-fit', onZoomFit);
    window.addEventListener('canvas-zoom-reset', onZoomReset);
    window.addEventListener('canvas-zoom-in', onZoomIn);
    window.addEventListener('canvas-zoom-out', onZoomOut);

    return () => {
      window.removeEventListener('canvas-center-view', onCenterView);
      window.removeEventListener('canvas-zoom-fit', onZoomFit);
      window.removeEventListener('canvas-zoom-reset', onZoomReset);
      window.removeEventListener('canvas-zoom-in', onZoomIn);
      window.removeEventListener('canvas-zoom-out', onZoomOut);
    };
  }, [handleCenterCanvas, handleFitToScreen, handleZoomReset, handleZoomIn, handleZoomOut]);

  return (
    <div className="canvas-navigation-bar">
      <div className="nav-group">
        <button
          className="nav-btn"
          onClick={handleZoomOut}
          title="Zoom arrière (Ctrl+-)"
          aria-label="Zoom arrière"
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
            <line x1="8" y1="11" x2="14" y2="11" />
          </svg>
        </button>

        {/* AMÉLIORATION: Slider de zoom interactif */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: '120px' }}>
          <input
            type="range"
            min="10"
            max="500"
            step="5"
            value={Math.round(zoomLevel * 100)}
            onChange={handleZoomSliderChange}
            style={{
              flex: 1,
              height: '4px',
              borderRadius: '2px',
              background: 'var(--color-border, #e2e8f0)',
              outline: 'none',
              cursor: 'pointer',
            }}
            title={`Zoom: ${Math.round(zoomLevel * 100)}%`}
            aria-label="Niveau de zoom"
          />
          <span
            className="zoom-display"
            style={{
              minWidth: '50px',
              textAlign: 'right',
              fontSize: '12px',
              fontWeight: 500,
              color: 'var(--color-text-primary, #1e293b)',
            }}
          >
            {Math.round(zoomLevel * 100)}%
          </span>
        </div>

        <button
          className="nav-btn"
          onClick={handleZoomIn}
          title="Zoom avant (Ctrl++)"
          aria-label="Zoom avant"
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
            <line x1="11" y1="8" x2="11" y2="14" />
            <line x1="8" y1="11" x2="14" y2="11" />
          </svg>
        </button>
      </div>

      <div className="nav-separator" />

      <div className="nav-group">
        <button
          className="nav-btn"
          onClick={handleZoomReset}
          title="Réinitialiser le zoom (100%)"
          aria-label="Réinitialiser le zoom"
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
            <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
          </svg>
        </button>
        <button
          className="nav-btn"
          onClick={handleFitToScreen}
          title="Adapter à l'écran"
          aria-label="Adapter à l'écran"
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" />
          </svg>
        </button>
        <button
          className="nav-btn"
          onClick={handleCenterCanvas}
          title="Centrer le canvas (Home)"
          aria-label="Centrer le canvas"
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="16" />
            <line x1="8" y1="12" x2="16" y2="12" />
          </svg>
        </button>
        {/* AMÉLIORATION: Bouton zoom vers sélection */}
        <button
          className="nav-btn"
          onClick={handleZoomToSelection}
          title="Zoom vers la sélection (Ctrl+0)"
          aria-label="Zoom vers la sélection"
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <rect x="3" y="3" width="7" height="7" />
            <rect x="14" y="3" width="7" height="7" />
            <rect x="14" y="14" width="7" height="7" />
            <rect x="3" y="14" width="7" height="7" />
          </svg>
        </button>
      </div>

      <div className="nav-separator" />

      <div className="nav-info">
        <span className="nav-info-text">
          {templateWidth} × {templateHeight}px
        </span>
      </div>
    </div>
  );
}
